import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import type { LiveView, PlacarDaRodada } from "../game/types";
import { Crest } from "../ui/atoms";
import { Icone } from "../ui/icons";
import { TrocaNoBanco, type JogadorDaTroca } from "../ui/TrocaNoBanco";
import { tocarGol } from "../ui/somDeGol";
import { useDialogoDeAtalhos } from "../ui/useAtalhos";
import { lanceDaPartida, rotuloDeFase, useT, type Chave } from "../i18n";

/**
 * Partida ao vivo, em texto.
 *
 * É a herança mais direta do Elifoot: os lances chegam um a um e o treinador
 * acompanha o jogo acontecer. A partida para sozinha no intervalo, o "Pausar"
 * para quando ele quiser, e nas duas dá para mexer no time. A qualquer momento
 * dá para pular para o resultado.
 *
 * Assistir não muda nada. O motor simula a mesma partida, nos mesmos pedaços, e
 * há teste garantindo que a rodada assistida devolve um mundo idêntico ao da
 * rodada pulada. Quem não gosta de ver joga no botão de pular sem prejuízo.
 */

/**
 * Ordem das posições em campo, do gol ao ataque.
 *
 * Serve para ordenar o banco por utilidade na hora da troca. Sem isso a lista
 * vinha na ordem em que o elenco foi montado, e o primeiro nome oferecido para
 * substituir um atacante podia ser um goleiro.
 */
const ORDEM_POSICAO: Record<string, number> = {
  GOL: 0,
  ZAG: 1,
  LD: 2,
  LE: 2,
  VOL: 3,
  MC: 4,
  MEI: 5,
  PD: 6,
  PE: 6,
  ATA: 7,
};

/** Quão bem um reserva cobre a vaga de quem está saindo. Menor é melhor. */
function distanciaDaVaga(reserva: string, vaga: string): number {
  const a = ORDEM_POSICAO[reserva] ?? 4;
  const b = ORDEM_POSICAO[vaga] ?? 4;
  // Goleiro só entra no lugar de goleiro, e vice-versa.
  if ((reserva === "GOL") !== (vaga === "GOL")) return 99;
  return Math.abs(a - b);
}

/** Ritmos de exibição, em milissegundos por pedaço de cinco minutos de jogo. */
const RITMOS: Array<{ rotulo: Chave; ms: number }> = [
  { rotulo: "aoVivo.ritmo1x", ms: 1400 },
  { rotulo: "aoVivo.ritmo2x", ms: 700 },
  { rotulo: "aoVivo.ritmo4x", ms: 350 },
];

export function LiveMatch({
  live,
  busy,
  onStep,
  onSub,
  onFinish,
}: {
  live: LiveView;
  busy: boolean;
  onStep: (untilSegment: number) => void;
  onSub: (outId: string, inId: string) => void;
  onFinish: () => void;
}) {
  const t = useT();
  const [ritmo, setRitmo] = useState(0);
  const [tocando, setTocando] = useState(true);
  const fimDaLista = useRef<HTMLDivElement | null>(null);

  const acabou = live.finished || live.segment >= live.totalSegments;
  /*
   * Pausa só existe com jogo pela frente.
   *
   * O motor para no fim de cada trecho, o último incluído, e ali `finished`
   * ainda pode estar falso. Com a pausa olhando só para `finished`, o fim de
   * jogo abria o painel de substituição com "Voltar para o jogo", e não havia
   * jogo nenhum para voltar.
   */
  const emPausa = live.atPause && !acabou;
  // A substituição abre no intervalo e no "Pausar": é a mesma pausa.
  const trocando = !acabou && (emPausa || !tocando);

  /*
   * Com o jogo rolando, o teclado é do jogo: espaço pausa, os números mudam o
   * ritmo e Esc pula para o resultado, que é a única saída desta tela. Enter só
   * vale onde existe um botão esperando, para ninguém pular a partida sem querer.
   */
  useDialogoDeAtalhos({
    nome: "Partida ao vivo",
    nivel: 28,
    aoFechar: () => {
      if (!busy) onFinish();
    },
    atalhos: {
      espaco: () => setTocando((valor) => !valor),
      "1": () => setRitmo(0),
      "2": () => setRitmo(1),
      "3": () => setRitmo(2),
      enter: () => {
        if (busy) return;
        if (trocando) {
          voltarAoJogo();
          return;
        }
        if (acabou) onFinish();
      },
    },
  });

  // Relógio da partida. Para nas pausas e quando o treinador pede.
  useEffect(() => {
    if (acabou || emPausa || !tocando || busy) return;
    const ms = RITMOS[ritmo]?.ms ?? 1400;
    const id = window.setTimeout(() => onStep(live.segment + 1), ms);
    return () => window.clearTimeout(id);
  }, [acabou, emPausa, tocando, busy, ritmo, live.segment, onStep]);

  function voltarAoJogo(): void {
    setTocando(true);
    if (emPausa) onStep(live.segment + 1);
  }

  // Gol novo toca o som, o do meu time diferente do gol contra. Os lances que
  // já estavam na tela ao abrir não tocam: só o que acontece daqui em diante.
  const lancesVistos = useRef(live.events.length);
  useEffect(() => {
    const novos = live.events.slice(lancesVistos.current);
    lancesVistos.current = live.events.length;
    const gol = novos.filter((evento) => evento.kind === "gol").at(-1);
    if (gol) tocarGol(gol.clubId === live.myClubId);
  }, [live.events, live.myClubId]);

  // A lista de lances rola sozinha enquanto o jogo corre.
  useEffect(() => {
    fimDaLista.current?.scrollIntoView({ block: "end" });
  }, [live.events.length]);

  // Quem sai: os mais cansados primeiro, que é a decisão que o treinador está
  // tomando. Quem entra: do mais forte ao menos e, escolhido quem sai, quem
  // cobre a vaga dele, com o goleiro no fim a menos que a vaga seja no gol.
  const emCampo: JogadorDaTroca[] = [...live.onPitch]
    .sort((a, b) => a.condition - b.condition)
    .map((jogador) => ({ ...jogador, pos: jogador.slot }));
  const banco: JogadorDaTroca[] = [...live.bench].sort((a, b) => b.strength - a.strength);
  const ordenarBanco = (reservas: JogadorDaTroca[], saindo: JogadorDaTroca): JogadorDaTroca[] =>
    [...reservas].sort(
      (a, b) =>
        distanciaDaVaga(a.pos, saindo.pos) - distanciaDaVaga(b.pos, saindo.pos) || b.strength - a.strength,
    );

  const [aba, setAba] = useState<"lances" | "rodada">("lances");
  const [expandido, definirExpandido] = useState(expandidoGuardado);

  const meuGol = live.iAmHome ? live.homeGoals : live.awayGoals;
  const golDele = live.iAmHome ? live.awayGoals : live.homeGoals;
  const corPlacar =
    meuGol > golDele ? "var(--success)" : meuGol < golDele ? "var(--danger)" : "var(--text)";
  const emPontosCorridos = live.stage === "liga" || live.stage === "estadual";

  return (
    <div
      role="dialog"
      aria-label={t("aoVivo.aria")}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.9)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "var(--gutter)",
        zIndex: 28,
      }}
    >
      <div
        className="card"
        style={{
          /* O botão de expandir é absoluto, e precisa desta caixa como origem. */
          position: "relative",
          width: expandido ? "min(1500px, 100%)" : "min(720px, 100%)",
          maxHeight: "92vh",
          overflow: "auto",
          padding: 18,
        }}
      >
        {/*
          Expandir é o que faz a aba da rodada valer.

          Em 720 pixels os oitenta jogos de uma rodada cheia viram uma fila de
          duas mil pixels dentro de uma caixa de quatrocentos. Expandido, eles
          fluem em três ou quatro colunas e a rodada inteira cabe sem rolar.

          O botão fica no alto e à direita, fora da linha do placar: ali ele não
          disputa espaço com o nome dos dois clubes, que é o que a caixa existe
          para mostrar.
        */}
        <button
          className="ghost aovivo-expandir"
          onClick={() => {
            definirExpandido((antes) => {
              guardarExpandido(!antes);
              return !antes;
            });
          }}
          aria-pressed={expandido}
          aria-label={t(expandido ? "aoVivo.encolher" : "aoVivo.expandir")}
          title={t(expandido ? "aoVivo.encolher" : "aoVivo.expandir")}
        >
          <Icone nome={expandido ? "Encolher" : "Expandir"} size={15} />
        </button>
        <div className="label-muted" style={{ textAlign: "center" }}>
          {emPontosCorridos ? live.competition : `${live.competition} · ${rotuloDeFase(live.stage)}`}
        </div>

        <div
          className="row"
          style={{ gap: 14, justifyContent: "center", alignItems: "center", margin: "10px 0 4px" }}
        >
          <div className="row" style={{ gap: 8, flex: 1, justifyContent: "flex-end", minWidth: 0 }}>
            <span style={{ fontWeight: 700, textAlign: "right" }}>{live.homeName}</span>
            <Crest colors={live.homeColors} short={live.homeShort} size={34} />
          </div>
          <span className="num" style={{ fontSize: 30, fontWeight: 800, color: corPlacar }}>
            {live.homeGoals} x {live.awayGoals}
          </span>
          <div className="row" style={{ gap: 8, flex: 1, minWidth: 0 }}>
            <Crest colors={live.awayColors} short={live.awayShort} size={34} />
            <span style={{ fontWeight: 700 }}>{live.awayName}</span>
          </div>
        </div>

        <div style={{ textAlign: "center", marginBottom: 12 }}>
          <span className="num" style={{ fontSize: 15, fontWeight: 700, color: "var(--primary)" }}>
            {acabou ? t("aoVivo.fimDeJogo") : t("aoVivo.minuto", { minuto: live.minute })}
          </span>
        </div>

        <div
          style={{
            height: 4,
            borderRadius: 999,
            background: "var(--surface-3)",
            overflow: "hidden",
            marginBottom: 14,
          }}
        >
          <div
            style={{
              width: `${Math.min(100, (live.segment / live.totalSegments) * 100)}%`,
              height: "100%",
              background: "var(--primary)",
              transition: "width 300ms linear",
            }}
          />
        </div>

        {/*
          Duas abas no mesmo lugar, e não duas listas empilhadas.

          A narração e os outros placares querem o mesmo espaço da tela: a
          rodada tem dezenas de jogos e a narração cresce a cada minuto. Uma
          embaixo da outra faria as duas caberem pela metade, e quem está
          olhando uma não está lendo a outra.
        */}
        <div className="row" style={{ gap: 8, marginBottom: 8 }}>
          {(["lances", "rodada"] as const).map((qual) => (
            <button
              key={qual}
              className="ghost"
              aria-pressed={aba === qual}
              onClick={() => setAba(qual)}
              style={
                aba === qual
                  ? { borderColor: "var(--primary)", color: "var(--primary)", fontWeight: 700 }
                  : undefined
              }
            >
              {qual === "lances" ? t("aoVivo.abaLances") : t("aoVivo.abaRodada")}
            </button>
          ))}
        </div>

        <div
          className="scroll"
          style={{
            maxHeight: emPausa ? "26vh" : expandido ? "58vh" : "44vh",
            background: "var(--surface-2)",
            borderRadius: 8,
            padding: "8px 10px",
          }}
        >
          {aba === "rodada" ? (
            <PlacaresDaRodada placares={live.placares} />
          ) : live.events.length === 0 ? (
            <p className="muted" style={{ margin: 0, fontSize: 12 }}>
              {t("aoVivo.bolaVaiRolar")}
            </p>
          ) : (
            live.events.map((evento, index) => (
              <div
                key={`${evento.minute}-${index}`}
                className="row"
                style={{ gap: 10, padding: "3px 0", alignItems: "baseline", ...estiloDoGol(evento.kind === "gol", evento.clubId === live.myClubId) }}
              >
                <span
                  className="num"
                  style={{ width: 30, color: "var(--text-dim)", fontSize: 11.5, flexShrink: 0 }}
                >
                  {t("aoVivo.minuto", { minuto: evento.minute })}
                </span>
                <span
                  style={{
                    fontSize: 12.5,
                    fontWeight: evento.kind === "gol" ? 700 : 400,
                    color:
                      evento.kind === "gol"
                        ? evento.clubId === live.myClubId
                          ? "var(--success)"
                          : "var(--danger)"
                        : evento.kind === "vermelho"
                          ? "var(--danger)"
                          : evento.kind === "amarelo"
                            ? "var(--warning)"
                            : "inherit",
                  }}
                >
                  {lanceDaPartida(evento.key, evento.params)}
                </span>
              </div>
            ))
          )}
          <div ref={fimDaLista} />
        </div>

        {trocando && (
          <div style={{ marginTop: 14, borderTop: "1px solid var(--border)", paddingTop: 12 }}>
            <div className="row" style={{ justifyContent: "space-between", gap: 10 }}>
              <span className="label">{emPausa ? t("aoVivo.intervalo") : t("aoVivo.pausado")}</span>
              <span className="label-muted">
                {t("aoVivo.restamTrocas", { trocas: live.subsLeft })}
              </span>
            </div>
            <div className="scroll" style={{ maxHeight: "30vh", marginTop: 10 }}>
              <TrocaNoBanco
                emCampo={emCampo}
                banco={banco}
                rotuloDoCampo={t("aoVivo.emCampo")}
                rotuloDoBanco={t("aoVivo.banco")}
                vazioDoCampo={t("aoVivo.ninguemEmCampo")}
                vazioDoBanco={t("aoVivo.semReservas")}
                bloqueado={busy || live.subsLeft === 0}
                aoTrocar={onSub}
                ordenarBanco={ordenarBanco}
              />
            </div>

            <div className="row" style={{ gap: 8, marginTop: 10 }}>
              <button
                className="primary"
                style={{ flex: 1 }}
                disabled={busy}
                onClick={voltarAoJogo}
              >
                {t("aoVivo.voltarParaOJogo")}
              </button>
            </div>
          </div>
        )}

        <div className="row" style={{ gap: 8, marginTop: 14, flexWrap: "wrap" }}>
          {!acabou && !trocando && (
            <>
              <button className="ghost" onClick={() => setTocando((v) => !v)} disabled={busy}>
                {tocando ? t("aoVivo.pausar") : t("comum.continuar")}
              </button>
              {RITMOS.map((entrada, index) => (
                <button
                  key={entrada.rotulo}
                  className="ghost"
                  onClick={() => setRitmo(index)}
                  disabled={busy}
                  style={
                    ritmo === index
                      ? { borderColor: "var(--primary)", color: "var(--primary)", fontWeight: 700 }
                      : undefined
                  }
                >
                  {t(entrada.rotulo)}
                </button>
              ))}
            </>
          )}
          <button
            className={acabou ? "primary" : "ghost"}
            style={{ flex: 1, minWidth: 140 }}
            disabled={busy}
            onClick={onFinish}
          >
            {acabou ? t("aoVivo.verResumo") : t("aoVivo.pularParaResultado")}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Os outros jogos da rodada, agrupados por competição.
 *
 * Existe porque o técnico não dirige num vácuo: enquanto o jogo dele corre, o
 * rival está jogando, e o resultado de lá muda a tabela tanto quanto o de cá.
 *
 * **Flui em colunas, e não numa lista só.** São oitenta jogos numa rodada
 * cheia, e numa lista única eles pedem duas mil pixels de rolagem dentro de uma
 * caixa de quatrocentos. `column-width` deixa o navegador fazer quantas colunas
 * couberem, e é por isso que expandir a partida resolve de verdade em vez de só
 * esticar a mesma fila.
 *
 * Cada competição é uma seção com `break-inside: avoid`, senão o nome do
 * campeonato termina no pé de uma coluna e os jogos dele começam no topo da
 * seguinte.
 */
/*
 * Expandido ou não, a escolha fica guardada.
 *
 * Quem expandiu expandiu porque tem monitor para isso, e a partida abre uma vez
 * por rodada: reabrir pequena toda vez seria refazer a escolha setenta vezes
 * por temporada. Em try/catch como as outras preferências.
 */
const CHAVE_DO_TAMANHO = "lm:aovivo";

function expandidoGuardado(): boolean {
  try {
    return localStorage.getItem(CHAVE_DO_TAMANHO) === "expandido";
  } catch {
    return false;
  }
}

function guardarExpandido(expandido: boolean): void {
  try {
    localStorage.setItem(CHAVE_DO_TAMANHO, expandido ? "expandido" : "normal");
  } catch {
    // Sem lugar para guardar. Vale até fechar a aba, e pronto.
  }
}

function PlacaresDaRodada({ placares }: { placares: PlacarDaRodada[] }): ReactNode {
  const grupos: Array<{ competicao: string; jogos: PlacarDaRodada[] }> = [];
  for (const jogo of placares) {
    const ultimo = grupos[grupos.length - 1];
    if (ultimo && ultimo.competicao === jogo.competicao) ultimo.jogos.push(jogo);
    else grupos.push({ competicao: jogo.competicao, jogos: [jogo] });
  }

  return (
    <div className="placares">
      {grupos.map((grupo, indice) => (
        <section key={`${grupo.competicao}-${String(indice)}`} className="placares-grupo">
          <div className="label-muted" style={{ padding: "2px 0 5px" }}>
            {grupo.competicao}
          </div>
          {grupo.jogos.map((jogo) => (
            <LinhaDePlacar key={jogo.fixtureId} jogo={jogo} />
          ))}
        </section>
      ))}
    </div>
  );
}

function LinhaDePlacar({ jogo }: { jogo: PlacarDaRodada }): ReactNode {
  const t = useT();
  return (
    <div
      className="row placares-linha"
      {...(jogo.minha ? { "data-minha": "" } : {})}
      title={`${jogo.homeName} ${jogo.homeGoals} x ${jogo.awayGoals} ${jogo.awayName}`}
    >
      <span className="num placares-minuto">
        {jogo.finished ? t("aoVivo.jogoAcabou") : t("aoVivo.minuto", { minuto: jogo.minute })}
      </span>
      <span className="corta" style={{ flex: 1, textAlign: "right" }}>
        {jogo.homeName}
      </span>
      <Crest colors={jogo.homeColors} short={jogo.homeShort} size={18} />
      <span className="num" style={{ fontWeight: 800, flexShrink: 0 }}>
        {jogo.homeGoals} x {jogo.awayGoals}
      </span>
      <Crest colors={jogo.awayColors} short={jogo.awayShort} size={18} />
      <span className="corta" style={{ flex: 1 }}>
        {jogo.awayName}
      </span>
    </div>
  );
}

/**
 * O fundo da linha do gol: verde para o meu, vermelho para o contra.
 *
 * A cor do texto sozinha confundia o gol contra com a lesão, que também é
 * vermelha. O fundo faz o gol saltar da lista, dos dois lados.
 */
export function estiloDoGol(ehGol: boolean, aFavor: boolean): CSSProperties {
  if (!ehGol) return {};
  return {
    background: aFavor ? "var(--primary-glow)" : "rgba(229, 72, 77, 0.16)",
    borderRadius: 4,
    padding: "3px 6px",
    margin: "1px -6px",
  };
}
